import {ErrorMessages} from 'constants/Errors';
import {ModalComponents} from 'constants/Modals';
import {Routes} from 'constants/routes';
import {SuccessMessages} from 'constants/Success';

import {AxiosResponse} from 'axios';
import {call, put, select} from 'redux-saga/effects';
import {changePass, getUserData, login, register, resetPass, updateUserData} from 'services/auth-service';
import {getManagedPubACS} from 'store/managedPub/requestAC';
import {setModalAC} from 'store/modal/modalSlice';
import ApiData from 'typescript/interfaces/ApiData';
import User from 'typescript/models/User';

import {
  setErrorAC,
  setLoadingAC,
  setPasswordResetEmailAC,
  setRegisteredEmailAC,
  setSuccessAC,
  setUserAC,
  setValidationErrorAC,
} from '../appSlice';
import {handleError} from '../error/saga';
import {
  changePasswordACS,
  getUserACS,
  loginUserACS,
  registerUserACS,
  resetPasswordACS,
  updateUserACS,
} from '../requestAC';

export function* getUser(action: ReturnType<typeof getUserACS>) {
  try {
    const response: AxiosResponse<ApiData<User>> = yield call(getUserData, +action.payload);

    yield put(setUserAC(response.data.data));
  } catch (error) {
    yield call(handleError, error, 'Failed to get user data');
  }
}

export function* loginUser(action: ReturnType<typeof loginUserACS>): Generator<unknown, void, any> {
  const {loginData, navigate} = action.payload;

  try {
    yield put(setLoadingAC(true));
    const response = yield call(login, loginData);

    const user = response.data;

    if (!user.admin) {
      yield put(setErrorAC('Please download the app to login'));
      return;
    }

    const token = response.headers.authorization;
    localStorage.setItem('token', JSON.stringify(token));

    const userData = yield call(getUserData, user.id);
    const {attributes} = userData.data.data;

    yield put(setUserAC(userData.data.data));
    localStorage.setItem('user', JSON.stringify(userData.data.data));

    const hasPubs = attributes.pubs.length > 0;

    if (hasPubs) {
      const firstPubId = attributes.pubs[0].id;
      yield put(getManagedPubACS(firstPubId));
    }

    if (!hasPubs && !attributes.admin) {
      navigate(Routes.PROFILE);
      return;
    }

    navigate(Routes.HOME);

    const navigation = document.querySelector('.navbar');
    navigation?.scrollIntoView();
  } catch (error) {
    yield put(setErrorAC(ErrorMessages.LOGIN_ERROR));
  } finally {
    yield put(setLoadingAC(false));
  }
}

export function* registerUser(action: ReturnType<typeof registerUserACS>) {
  const registerData = action.payload!;

  const {
    validationError: {email, nickname},
  } = yield select((state) => state.app);

  try {
    yield call(register, registerData);

    yield put(setRegisteredEmailAC(registerData.email));
    yield put(setModalAC(ModalComponents.REGISTRATION_SUCCESS));

    if (email || nickname) setValidationErrorAC({email: '', nickname: ''});
  } catch (error: any) {
    let inputErrors: any = {};
    if (Array.isArray(error.response?.data.errors)) {
      error.response?.data.errors.forEach((err: any) => {
        if (err.source) inputErrors[err.source] = err.title;
        else inputErrors = err;
      });
    } else {
      Object.entries(error.response?.data.errors).forEach((key: any) => {
        const firstLetterCapital = key[0][0].toUpperCase();
        const wholeWord = `${firstLetterCapital + key[0].slice(1)} ${key[1]}`;
        [inputErrors[key[0]]] = [wholeWord];
      });
    }

    const {email, nickname} = inputErrors;
    if (email || nickname) yield put(setValidationErrorAC(inputErrors));
  }
}

export function* resetPassword(action: ReturnType<typeof resetPasswordACS>) {
  const {data, isResent} = action.payload;

  try {
    yield call(resetPass, data);
    if (!isResent) {
      yield put(setModalAC(ModalComponents.RESET_PASSWORD));
      yield put(setPasswordResetEmailAC(data.email));
    }
  } catch (error) {
    yield call(handleError, error, ErrorMessages.FORGOT_PASSWORD);
  }
}

export function* changePassword(action: ReturnType<typeof changePasswordACS>) {
  const changePasswordData = action.payload;

  try {
    yield call(changePass, changePasswordData);

    yield put(setSuccessAC(SuccessMessages.PASSWORD_CHANGE));
  } catch (error) {
    yield call(handleError, error, ErrorMessages.OLD_PASSWORD);
  }
}

export function* updateUser(action: ReturnType<typeof updateUserACS>) {
  const {id, data} = action.payload;

  try {
    yield call(updateUserData, id, data);
    yield put(getUserACS(id));
    yield put(setSuccessAC(SuccessMessages.UPDATE_USER));
  } catch (error) {
    yield call(handleError, error, ErrorMessages.UPDATE_USER);
  }
}
